/* ---------------------------------------------------------------------------
   Construction Monitoring: one structure's works, a month at a time.

   The other way to draw the Works page, chosen by the switch in its panel head
   (`worksLayout` in the store). Each day carries the work done on it — what and
   where — so a busy fortnight and an idle one read as such before anybody reads
   a row. An entry not yet verified is marked on the day, as it is in the table.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS, type IconPath } from '../core/dom.ts';
import { actions, type State, type WorksLayout } from '../core/store.ts';
import { seg } from '../components/widgets.ts';
import { activityLabel } from '../data/activities.ts';
import { prettyDate } from '../data/derive.ts';
import type { ProgressEntry } from '../data/types.ts';
import { entryTiming, type EntryTiming } from '../data/variance.ts';
import { progressQuantity, progressWhere } from './works.ts';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/** Entries a day shows before the rest are counted as "+N more". */
const SHOWN = 4;

/** Today, as a calendar day. The one place this module reads the clock. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function monthName(month: string): string {
  const [y, m] = month.split('-');
  return `${MONTHS[Number(m) - 1]} ${y}`;
}

/** The month before or after, as `yyyy-mm`. */
function stepMonth(month: string, by: 1 | -1): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return d.toISOString().slice(0, 7);
}

/** One entry as a line of the day's title. */
function entryLine(e: ProgressEntry): string {
  const qty = progressQuantity(e);
  return `${activityLabel(e.activity)} · ${progressWhere(e)}${qty ? ` · ${qty}` : ''}${e.state === 'accepted' ? '' : ' (not verified)'}`;
}

/** The panel header's control: table or calendar. */
export function worksActions(s: Readonly<State>): HTMLElement[] {
  const layouts: { layout: WorksLayout; label: string; lead: IconPath; title: string }[] = [
    { layout: 'table', label: 'Table', lead: ICONS.list, title: 'Show the works as a table' },
    { layout: 'calendar', label: 'Calendar', lead: ICONS.calendar, title: 'Show the works on a month calendar' },
  ];
  const el = seg(
    layouts.map((l) => ({
      label: l.label,
      leadIcon: l.lead,
      title: l.title,
      on: s.worksLayout === l.layout,
      onClick: () => actions.setWorksLayout(l.layout),
    })),
  );
  el.classList.add('seg--head');
  el.setAttribute('aria-label', 'Show as');
  /* The key to the calendar's colours, beside the switch, where there is a programme to colour by. */
  const legend =
    s.worksLayout === 'calendar' && s.baseline !== null
      ? h(
          'div',
          { class: 'wcal__legend', 'aria-label': 'Colour key' },
          ...(['ahead', 'ontime', 'late'] as const).map((t) =>
            h('span', { class: 'wcal__key' }, h('span', { class: `wcal__swatch wcal__swatch--${t}` }), TIMING_WORD[t]),
          ),
        )
      : null;
  return legend ? [legend, el] : [el];
}

const TIMING_WORD: Record<EntryTiming, string> = { ahead: 'Ahead of schedule', ontime: 'On time', late: 'Late' };

/** The month's works as a calendar, or null where there is nothing to draw. */
export function worksCalendar(s: Readonly<State>): HTMLElement | null {
  if (s.progressRead !== 'read' || s.progress.length === 0) return null;

  const byDay = new Map<string, ProgressEntry[]>();
  for (const e of s.progress) {
    const list = byDay.get(e.occurredOn);
    if (list) list.push(e);
    else byDay.set(e.occurredOn, [e]);
  }
  const days = [...byDay.keys()].sort();
  const now = today();
  const first = days[0]!.slice(0, 7);
  const latest = days[days.length - 1]!.slice(0, 7);
  const last = now.slice(0, 7) > latest ? now.slice(0, 7) : latest;
  const month = s.worksMonth !== null && s.worksMonth >= first && s.worksMonth <= last ? s.worksMonth : latest;

  const start = `${month}-01`;
  const lead = (new Date(`${start}T00:00:00Z`).getUTCDay() + 6) % 7;
  const [y, m] = month.split('-').map(Number) as [number, number];
  const length = new Date(Date.UTC(y, m, 0)).getUTCDate();

  const baseline = s.baseline;
  const timing = (e: ProgressEntry): EntryTiming | null =>
    baseline === null
      ? null
      : entryTiming(baseline, s.matches, {
          component: e.component,
          componentId: e.componentId ?? null,
          occurredOn: e.occurredOn,
          activity: e.activity,
        });

  const cells: HTMLElement[] = [];
  for (let i = 0; i < lead; i++) cells.push(h('div', { class: 'wcal__day wcal__day--pad' }));
  let entries = 0;
  let unverified = 0;
  let worked = 0;
  for (let d = 1; d <= length; d++) {
    const iso = `${month}-${String(d).padStart(2, '0')}`;
    const list = byDay.get(iso) ?? [];
    entries += list.length;
    unverified += list.filter((e) => e.state !== 'accepted').length;
    if (list.length) worked++;

    const title = list.length ? [prettyDate(iso), ...list.map(entryLine)].join('\n') : prettyDate(iso);
    cells.push(
      h(
        'div',
        {
          class: ['wcal__day', iso > now && 'wcal__day--future', iso === now && 'wcal__day--today'],
          title,
        },
        h('span', { class: 'wcal__num num', text: String(d) }),
        ...list.slice(0, list.length > SHOWN ? SHOWN - 1 : SHOWN).map((e) => {
          const t = timing(e);
          return h(
            'button',
            {
              type: 'button',
              class: ['wcal__ev', e.state !== 'accepted' && `wcal__ev--${e.state}`, t && `wcal__ev--${t}`, e.pending && 'is-muted'],
              title: t ? `${entryLine(e)}\n${TIMING_WORD[t]}` : entryLine(e),
              onclick: () => actions.openWorksEntry(e.id),
            },
            h('span', { class: 'wcal__ev-what', text: activityLabel(e.activity) }),
            h('span', { class: 'wcal__ev-where', text: progressWhere(e) }),
          );
        }),
        list.length > SHOWN
          ? h('button', {
              type: 'button',
              class: 'wcal__more',
              text: `+${list.length - SHOWN + 1} more`,
              onclick: () => actions.openWorksEntry(list[SHOWN - 1]!.id),
            })
          : null,
      ),
    );
  }
  while (cells.length % 7 !== 0) cells.push(h('div', { class: 'wcal__day wcal__day--pad' }));

  const notes = [
    `${entries} ${entries === 1 ? 'entry' : 'entries'} on ${worked} ${worked === 1 ? 'day' : 'days'}`,
    unverified ? `${unverified} not yet verified` : null,
  ].filter(Boolean);

  return h(
    'div',
    { class: 'pcal wcal' },
    h(
      'div',
      { class: 'pcal__bar' },
      h(
        'button',
        {
          type: 'button',
          class: 'pcal__step',
          title: 'Previous month',
          'aria-label': 'Previous month',
          disabled: month <= first,
          onclick: () => actions.setWorksMonth(stepMonth(month, -1)),
        },
        icon(ICONS.chevronRight, { size: 14, width: 1.8, class: 'pcal__back' }),
      ),
      h('h3', { class: 'pcal__month', text: monthName(month) }),
      h(
        'button',
        {
          type: 'button',
          class: 'pcal__step',
          title: 'Next month',
          'aria-label': 'Next month',
          disabled: month >= last,
          onclick: () => actions.setWorksMonth(stepMonth(month, 1)),
        },
        icon(ICONS.chevronRight, { size: 14, width: 1.8 }),
      ),
      h('span', { class: 'pcal__count num', text: notes.join(' · ') }),
    ),
    h(
      'div',
      { class: 'pcal__grid', 'aria-label': `Works in ${monthName(month)}` },
      ...WEEKDAYS.map((w) => h('div', { class: 'pcal__wd', text: w })),
      ...cells,
    ),
  );
}
